import { useAtom } from 'jotai';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { AlertCircle, ArrowRight, ExternalLink, Loader2, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/glitch-ui/empty-state';
import { GeneratingDots } from '@/components/glitch-ui/generating-dots';
import { landingPageByCampaignAtom } from '@/store';
import { campaignLandingPageQueryOptions, landingPageQueryOptions } from '@/lib/query-options';
import { createLandingPage, publishLandingPage, unpublishLandingPage } from '@/lib/apis';
import { toast } from '@/hooks/useToast';
import type { CampaignData, LandingPage } from '@/lib/types';
import { VibeEditBox } from './vibe-edit-box';
import { LeadsTable } from './leads-table';

export function LandingPageTab({ campaign }: { campaign: CampaignData }) {
  const queryClient = useQueryClient();
  const [pageMap, setPageMap] = useAtom(landingPageByCampaignAtom);
  // The atom survives in-session navigation; the by-campaign lookup survives hard reloads.
  const trackedId = pageMap[campaign.id] ?? null;
  const lookup = useQuery(campaignLandingPageQueryOptions(trackedId ? null : campaign.id, campaign.organization_id));
  const publicId = trackedId ?? lookup.data?.public_id ?? null;

  const { data: landingPage, isLoading, isError } = useQuery(landingPageQueryOptions(publicId, campaign.organization_id));

  const generate = useMutation({
    mutationFn: () => createLandingPage(campaign.id, campaign.organization_id),
    onSuccess: (page) => {
      setPageMap((prev) => ({ ...prev, [campaign.id]: page.public_id }));
      queryClient.setQueryData(['landing-page', page.public_id], page);
    },
    onError: (error) => {
      toast({
        title: 'Failed to generate landing page',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  if (!trackedId && lookup.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <GeneratingDots count={4} />
        <p className="text-gray-700 mt-4">Loading landing page...</p>
      </div>
    );
  }

  // No page exists for this campaign → offer generation.
  if (!publicId) {
    return (
      <EmptyState
        icon={<Sparkles className="h-12 w-12 text-[#290BD9]" strokeWidth={1} />}
        title="No Landing Page Yet"
        description="Generate a conversion-focused landing page from this campaign's keywords, ad copy, and brand."
        action={
          <Button
            size="lg"
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            className="w-[320px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg"
          >
            {generate.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                STARTING...
              </>
            ) : (
              <>
                <span>GENERATE LANDING PAGE</span>
                <ArrowRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        }
      />
    );
  }

  if (isLoading || !landingPage) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <GeneratingDots count={4} />
        <p className="text-gray-700 mt-4">Loading landing page...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <LandingPageError
        message="This landing page failed to load."
        onRegenerate={() => generate.mutate()}
        regenerating={generate.isPending}
      />
    );
  }

  if (landingPage.status === 'GENERATING') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <GeneratingDots count={4} />
        <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-1">Generating Landing Page</h3>
        <p className="text-gray-700">
          The page is being built from this campaign's keywords, ad copy, and brand. This can take a few minutes.
        </p>
      </div>
    );
  }

  if (landingPage.status === 'ERROR') {
    return (
      <LandingPageError
        message={landingPage.error_detail ?? 'Landing page generation failed.'}
        onRegenerate={() => generate.mutate()}
        regenerating={generate.isPending}
      />
    );
  }

  return (
    <LandingPagePreview
      landingPage={landingPage}
      organizationId={campaign.organization_id}
      onChanged={() => queryClient.invalidateQueries({ queryKey: ['landing-page', publicId] })}
    />
  );
}

function LandingPageError({
  message,
  onRegenerate,
  regenerating,
}: {
  message: string;
  onRegenerate: () => void;
  regenerating: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="border-l-4 border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C] p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold mb-1">Generation Failed</h3>
            <p className="text-sm">{message}</p>
          </div>
        </div>
      </div>
      <Button
        onClick={onRegenerate}
        disabled={regenerating}
        className="h-9 bg-[#290BD9] hover:bg-[#2309C5] text-white"
      >
        {regenerating ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Starting...
          </>
        ) : (
          'Regenerate'
        )}
      </Button>
    </div>
  );
}

function LandingPagePreview({
  landingPage,
  organizationId,
  onChanged,
}: {
  landingPage: LandingPage;
  organizationId: number;
  onChanged: () => void;
}) {
  const isPublished = landingPage.status === 'PUBLISHED';

  const publish = useMutation({
    mutationFn: () => publishLandingPage(landingPage.public_id, organizationId),
    onSuccess: onChanged,
    onError: (error) => toast({ title: 'Failed to publish', description: errMsg(error), variant: 'destructive' }),
  });
  const unpublish = useMutation({
    mutationFn: () => unpublishLandingPage(landingPage.public_id, organizationId),
    onSuccess: onChanged,
    onError: (error) => toast({ title: 'Failed to unpublish', description: errMsg(error), variant: 'destructive' }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {isPublished ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-[#D1FAE5] text-[#047857]">
              Live
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-[#EDE9FE] text-[#290BD9]">
              Preview
            </span>
          )}
          {landingPage.preview_url && (
            <a
              href={landingPage.preview_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-[#290BD9] hover:text-[#2309C5]"
            >
              Open in new tab
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isPublished ? (
            <Button
              variant="outline"
              onClick={() => unpublish.mutate()}
              disabled={unpublish.isPending}
              className="h-9 text-gray-900"
            >
              {unpublish.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Unpublish
            </Button>
          ) : (
            <Button
              onClick={() => publish.mutate()}
              disabled={publish.isPending}
              className="h-9 bg-green-600 hover:bg-green-700 text-white"
            >
              {publish.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  Publish
                  <ArrowRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {landingPage.preview_url && (
        <iframe
          src={landingPage.preview_url}
          title="Landing page preview"
          className="w-full h-[600px] rounded-lg border bg-white"
        />
      )}

      <VibeEditBox
        publicId={landingPage.public_id}
        organizationId={organizationId}
        sections={landingPage.sections}
        editInProgress={landingPage.edit_in_progress}
        lastEditError={landingPage.last_edit_error}
        onSubmitted={onChanged}
      />

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">Leads</h3>
        <LeadsTable publicId={landingPage.public_id} organizationId={organizationId} />
      </div>
    </div>
  );
}

function errMsg(error: unknown): string {
  return error instanceof Error ? error.message : 'An unknown error occurred';
}
